import { goToNextPrevStepOrAnySlide } from '@goldcast/api/backend';

import { getEnv } from '@/constants';
import loggerService from '@/services/logger.service';

/**
 * Updates a canvas element with a new slide image, using caching for performance
 *
 * @param canvasElement - The HTML canvas element to draw the slide on
 * @param canvasElementCache - A Map cache storing previously loaded slide images
 * @param imageUrl - URL of the slide image to display
 *
 * @remarks
 * - Checks cache first to avoid reloading images
 * - Creates new Image and loads it if not cached
 * - Clears canvas and draws new image maintaining aspect ratio
 * - Centers the image on canvas (portrait images will have black bars on sides)
 * - Handles CORS by setting crossOrigin to 'anonymous'
 * - Logs errors if image loading or drawing fails
 */
export async function updateSlidesCanvas(
  canvasElement: HTMLCanvasElement,
  canvasElementCache: Map<string, HTMLImageElement>,
  imageUrl: string | null
) {
  const ctx = canvasElement?.getContext('2d');
  if (!canvasElement || !ctx || !imageUrl) {
    return;
  }

  try {
    let img: HTMLImageElement;

    // Check if image is already cached
    if (canvasElementCache.has(imageUrl)) {
      img = canvasElementCache.get(imageUrl)!;
    } else {
      img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.crossOrigin = 'anonymous';
        img.src = imageUrl;
      });
      canvasElementCache.set(imageUrl, img);
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

    drawImageOnCanvas(ctx, img, canvasElement);
  } catch (error) {
    loggerService.error('[Local Recording] Slides: Error updating canvas with new image:', error);
  }
}

function drawImageOnCanvas(ctx: CanvasRenderingContext2D, img: HTMLImageElement, canvasElement: HTMLCanvasElement) {
  // Calculate dimensions to maintain aspect ratio
  const canvasWidth = canvasElement.width;
  const canvasHeight = canvasElement.height;
  const canvasAspectRatio = canvasWidth / canvasHeight;
  const imageAspectRatio = img.naturalWidth / img.naturalHeight;

  let drawWidth: number;
  let drawHeight: number;
  let drawX: number;
  let drawY: number;

  if (imageAspectRatio > canvasAspectRatio) {
    // Image is wider than canvas (landscape) - fit to width
    drawWidth = canvasWidth;
    drawHeight = canvasWidth / imageAspectRatio;
    drawX = 0;
    drawY = (canvasHeight - drawHeight) / 2;
  } else {
    // Image is taller than canvas (portrait) - fit to height
    drawHeight = canvasHeight;
    drawWidth = canvasHeight * imageAspectRatio;
    drawX = (canvasWidth - drawWidth) / 2;
    drawY = 0;
  }

  // Draw image centered with proper aspect ratio
  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
}

/**
 * Cleans up resources used for slide sharing by clearing intervals and caches
 *
 * @param rsSlidesCanvas - Object containing canvas elements and intervals to clean up
 * @param rsSlidesCanvas.canvasElement - The HTML canvas element to clear
 * @param rsSlidesCanvas.canvasElementCache - Map cache of slide images to clear
 * @param rsSlidesCanvas.slidesCanvasInterval - Optional interval ID to clear
 *
 * @remarks
 * - Stops the update interval if one exists
 * - Clears the canvas by erasing all content
 * - Clears the image cache to prevent memory leaks
 */
export function stopSharingSlidesCanvas(rsSlidesCanvas: {
  canvasElement: HTMLCanvasElement;
  canvasElementCache: Map<string, HTMLImageElement>;
  slidesCanvasInterval?: NodeJS.Timeout;
}) {
  clearInterval(rsSlidesCanvas.slidesCanvasInterval);

  // Clean up canvas element and cache to prevent memory leaks
  if (rsSlidesCanvas.canvasElement) {
    const canvas = rsSlidesCanvas.canvasElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
}

/**
 * Creates or initializes a canvas element for displaying slides
 *
 * @param slidesCanvas - Optional existing canvas configuration object
 * @param slidesCanvas.canvasElement - HTML canvas element for rendering slides
 * @param slidesCanvas.canvasElementCache - Cache map storing slide images
 * @returns The initialized canvas configuration object
 *
 * @remarks
 * - Creates new canvas and cache if not provided
 * - Sets canvas dimensions to 1920x1080 with device pixel ratio scaling
 * - Initializes 2D rendering context with proper scaling
 * - Used for rendering slides that can be shared via screen sharing
 */
export function getSlidesCanvas(slidesCanvas?: {
  canvasElement: HTMLCanvasElement;
  canvasElementCache: Map<string, HTMLImageElement>;
}) {
  if (!slidesCanvas) {
    slidesCanvas = {
      canvasElement: document.createElement('canvas'),
      canvasElementCache: new Map()
    };
  }
  if (!slidesCanvas.canvasElement) {
    slidesCanvas.canvasElement = document.createElement('canvas');
  }
  if (!slidesCanvas.canvasElementCache) {
    slidesCanvas.canvasElementCache = new Map();
  }

  slidesCanvas.canvasElement.width = 1920;
  slidesCanvas.canvasElement.height = 1080;

  return slidesCanvas;
}

export async function executeSlideNavigation(
  broadcast: { id: string; slide_state_version?: number | null },
  params: {
    action: 'next' | 'prev' | 'go_to_nth' | 'start' | 'stop';
    updatedSlide: number | null;
    updatedStep: number | null;
    currentPlayingSlideAsset: string | null;
  },
  onVersionConflict: (data: any) => void
) {
  try {
    return await goToNextPrevStepOrAnySlide({
      id: broadcast.id,
      body: {
        current_playing_slide_asset: params.currentPlayingSlideAsset,
        action: params.action,
        updated_slide_number: params.updatedSlide,
        slide_state_version: broadcast.slide_state_version ?? null,
        updated_step_number: params.updatedStep
      }
    });
  } catch (error: any) {
    if (error?.response?.data?.slide_state_version?.code === 'version_conflict') {
      onVersionConflict(error.response.data);
    }
    throw error;
  }
}

export function unshareSlidesOnLeave(broadcastId: string, userToken: string) {
  const url = `${getEnv('VUE_FETCHER_BACKEND_SERVER')}/event/broadcasts/${broadcastId}/update_slide/`;
  const payload = { current_playing_slide_number: 0, current_playing_slide_asset: null };

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`
    },
    body: JSON.stringify(payload),
    keepalive: true
  });
}
